import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../../i18n';
import aiAgentService from '../services/aiAgentService';
import useSpeechToText from './useSpeechToText';
import {
  DEFAULT_AGENT_MODULE_ID,
  getAgentModule,
  resolveAgentModuleFromPath,
  resolveAgentModuleFromAgentPath,
} from '../config/modules';
import {
  AGENT_MODE_STORAGE_KEY,
  DEFAULT_AGENT_MODE,
  getAgentMode,
} from '../config/agentModes';
import { isPlanUsable } from '../services/agentPlanFallback';
import {
  buildAgentMessageFromDocument,
  formatExtractedDocumentSummary,
  resolveDocumentTypeForModule,
} from '../utils/formatDocumentExtraction';
import {
  createAttachmentPreview,
  revokeAttachmentPreview,
  validateDocumentAttachmentFile,
} from '../utils/documentUpload';
import { saveHermesBridgeConfig } from '../config/hermesConfig';
import { isCloudDemoRuntime } from '../../shared/utils/publicDemoRuntime';
import {
  createAgentSession,
  deleteAgentSession,
  deriveSessionTitle,
  loadAgentSessions,
  serializeEntries,
  upsertAgentSession,
} from '../utils/agentSessionStorage';
import { secureGetItem } from '../../shared/utils/secureStorage';
import {
  buildAgentDocumentDraftView,
  persistAgentDocumentDraft,
} from '../utils/agentDocumentDraftView';

const cp = (key, opts) => i18n.t(`modules.shared.aiAgent.chat.${key}`, opts);
const cs = (key, opts) => i18n.t(`modules.shared.aiAgent.chatService.${key}`, opts);
const ss = (key, opts) => i18n.t(`modules.shared.aiAgent.sessionStorage.${key}`, opts);

export function getAgentPlaceholder() {
  return cp('placeholder');
}

/** @deprecated Use getAgentPlaceholder() */
export const AGENT_PLACEHOLDER = getAgentPlaceholder();

export function routeContextLabel(pathname) {
  const agentPage = resolveAgentModuleFromAgentPath(pathname);
  if (agentPage) return agentPage.label;

  if (pathname.startsWith('/facturation') || pathname.startsWith('/f/')) {
    return cp('route_facturation');
  }
  if (pathname.startsWith('/inventaire')) return cp('route_inventaire');
  if (pathname.startsWith('/achats')) return cp('route_achats');
  if (pathname.startsWith('/ventes')) return cp('route_ventes');
  if (pathname.startsWith('/comptabilite')) return cp('route_comptabilite');
  if (pathname.startsWith('/rh')) return cp('route_rh');
  if (pathname.startsWith('/hr') || pathname.startsWith('/employees')) return cp('route_rh');
  if (pathname === '/' || pathname === '/dashboard') return cp('route_dashboard');
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : cp('route_fallback');
}

export default function useAIAgentChat({ activeModuleId, setActiveModuleId } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [licenseBlocked, setLicenseBlocked] = useState(false);
  const [entries, setEntries] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [isBuildingPlan, setIsBuildingPlan] = useState(false);
  const [activeDocumentDraft, setActiveDocumentDraft] = useState(null);
  const [isDocumentFormModalOpen, setIsDocumentFormModalOpen] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [agentMode, setAgentModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(AGENT_MODE_STORAGE_KEY);
      if (saved && getAgentMode(saved)) return saved;
    } catch {
      /* ignore storage errors */
    }
    return DEFAULT_AGENT_MODE;
  });
  const inputRef = useRef(null);
  const appendEntryRef = useRef(null);
  const activeSessionIdRef = useRef(null);
  const sessionOwnerRef = useRef(ss('default_owner'));
  const [recentSessions, setRecentSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);

  const setAgentMode = useCallback((modeId) => {
    const mode = getAgentMode(modeId);
    setAgentModeState(mode.id);
    try {
      localStorage.setItem(AGENT_MODE_STORAGE_KEY, mode.id);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const appendEntry = useCallback((entry) => {
    setEntries((prev) => [...prev, { ...entry, id: `${Date.now()}-${prev.length}` }]);
  }, []);

  appendEntryRef.current = appendEntry;

  const {
    isSupported: isVoiceSupported,
    isListening,
    isTranscribing,
    recordingMs,
    audioLevels,
    error: voiceError,
    startListening,
    stopListening,
    cancelListening,
  } = useSpeechToText({
    transcribe: async (base64Audio) => {
      const result = await aiAgentService.transcribeVoiceAudio(base64Audio, i18n.language || 'ar');
      return result.text;
    },
    onTranscript: (text) => {
      setInputMessage(text);
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
      }
    },
    onError: (message) => {
      appendEntryRef.current?.({ kind: 'error', text: message });
    },
  });

  const contextLabel = routeContextLabel(location.pathname);
  const moduleId = activeModuleId || DEFAULT_AGENT_MODULE_ID;
  const activeModule = getAgentModule(moduleId);
  const isVoiceSession = isListening || isTranscribing;

  activeSessionIdRef.current = activeSessionId;

  const refreshRecentSessions = useCallback(() => {
    setRecentSessions(loadAgentSessions(moduleId));
  }, [moduleId]);

  useEffect(() => {
    const loadHermes = async () => {
      if (typeof window !== 'undefined' && window.electronAPI?.hermes?.getConfig) {
        try {
          const cfg = await window.electronAPI.hermes.getConfig();
          if (cfg?.apiKey) saveHermesBridgeConfig(cfg);
        } catch {
          /* ignore */
        }
      }
    };
    loadHermes();
  }, []);

  useEffect(() => {
    const storedUser = secureGetItem('qawaem_user');
    sessionOwnerRef.current = storedUser?.username || ss('default_owner');
  }, []);

  useEffect(() => {
    setActiveSessionId(null);
    refreshRecentSessions();
  }, [moduleId, refreshRecentSessions]);

  useEffect(() => {
    if (!entries.length) return undefined;

    const timeoutId = window.setTimeout(() => {
      const owner = sessionOwnerRef.current;
      let sessionId = activeSessionIdRef.current;

      if (!sessionId) {
        const created = createAgentSession({
          moduleId,
          title: deriveSessionTitle(entries),
          owner,
          entries,
        });
        sessionId = created.id;
        setActiveSessionId(created.id);
      } else {
        upsertAgentSession(moduleId, sessionId, {
          title: deriveSessionTitle(entries),
          owner,
          entries: serializeEntries(entries),
        });
      }

      refreshRecentSessions();
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [entries, moduleId, refreshRecentSessions]);

  useEffect(() => {
    if (!setActiveModuleId) return;
    const resolved = resolveAgentModuleFromPath(location.pathname);
    if (resolved !== DEFAULT_AGENT_MODULE_ID) {
      setActiveModuleId(resolved);
    }
  }, [location.pathname, setActiveModuleId]);

  useEffect(() => {
    const boot = async () => {
      const ok = await aiAgentService.initialize();
      setIsReady(ok);
      setLicenseBlocked(!ok);
    };
    boot();
  }, []);

  useEffect(() => {
    if (isReady) {
      aiAgentService.syncVectorIndexInBackground().catch(() => {});
    }
  }, [isReady]);

  useEffect(() => {
    const onEvent = (event) => {
      if (event === 'message_sent') setIsProcessing(true);
      if (event === 'message_received' || event === 'message_error') {
        setIsProcessing(false);
      }
    };
    aiAgentService.addListener(onEvent);
    return () => aiAgentService.removeListener(onEvent);
  }, []);

  const handleSend = async (promptOverride) => {
    stopListening();
    const text = (typeof promptOverride === 'string' ? promptOverride : inputMessage).trim();
    const attachment = pendingAttachment;

    if ((!text && !attachment) || isProcessing) return;

    if (!isReady) {
      appendEntry({
        kind: 'error',
        text: isCloudDemoRuntime()
          ? cp('agent_unavailable')
          : cp('license_required'),
      });
      return;
    }

    setInputMessage('');
    setPendingAttachment(null);
    setAttachmentError('');
    setPendingConfirmation(null);
    setActiveDocumentDraft(null);
    setIsDocumentFormModalOpen(false);
    if (agentMode !== 'plan') {
      setPendingPlan(null);
    }

    appendEntry({
      kind: 'prompt',
      text: text || cp('attached_prompt'),
      imagePreviewUrl: attachment?.previewUrl,
      imageName: attachment?.name,
      attachmentKind: attachment?.kind,
    });

    setIsProcessing(true);

    let extractionSummary = '';
    if (attachment?.file) {
      let prepared;
      try {
        prepared = await prepareAttachmentForVision(attachment.file);
      } catch (error) {
        appendEntry({
          kind: 'error',
          text: error.message || cp('document_prepare_error'),
        });
        setIsProcessing(false);
        return;
      }

      const docType = resolveDocumentTypeForModule(moduleId);
      const extractResult = await aiAgentService.extractDocumentData(
        prepared.file,
        docType,
        prepared.mimeType,
        text,
      );

      if (extractResult.success) {
        extractionSummary = formatExtractedDocumentSummary(
          extractResult.extractedData,
          extractResult.confidence,
        );
        if (prepared.sourceKind === 'pdf' && prepared.pageCount > 1) {
          extractionSummary += `\n\n${cp('pdf_pages_analyzed', { count: prepared.pageCount })}`;
        }
        const resultLabel = text ? cp('image_analysis_label') : cp('extracted_data_label');
        appendEntry({
          kind: 'result',
          text: `${resultLabel}\n\n${extractionSummary}`,
          extractedData: extractResult.extractedData,
        });
      } else {
        appendEntry({
          kind: 'error',
          text: extractResult.error || cp('document_read_error'),
        });
        setIsProcessing(false);
        return;
      }
    }

    const agentMessage = buildAgentMessageFromDocument(text, extractionSummary);
    if (!agentMessage) {
      setIsProcessing(false);
      return;
    }

    const result = await aiAgentService.sendMessage(agentMessage, {
      moduleId,
      mode: agentMode,
      sessionId: activeSessionId,
    });

    if (result.success) {
      if (result.plan?.status === 'needs_clarification' && isPlanUsable(result.plan)) {
        appendEntry({
          kind: 'clarification',
          text: result.response,
          plan: result.plan,
        });
      } else if (result.plan?.status === 'ready' && isPlanUsable(result.plan)) {
        setPendingPlan(result.plan);
        appendEntry({
          kind: 'plan',
          text: result.response,
          plan: result.plan,
        });
      } else if (result.needsConfirmation && result.actionDetected && agentMode !== 'plan') {
        setPendingConfirmation({
          action: result.actionDetected,
          decisionPackage: result.decisionPackage || null,
          message:
            result.confirmationMessage ||
            result.response ||
            cp('confirm_action'),
        });
      } else {
        appendEntry({
          kind: 'result',
          text: result.response,
          plan: result.plan || null,
          executionResult: result.executionResult,
        });
      }
    } else {
      appendEntry({
        kind: 'error',
        text: result.error || cs('communication_error'),
      });
    }

    setIsProcessing(false);
  };

  const handleOpenDocumentEditor = useCallback(
    (draftOrPath) => {
      const draft =
        typeof draftOrPath === 'string'
          ? activeDocumentDraft?.openPath === draftOrPath
            ? activeDocumentDraft
            : { openPath: draftOrPath, conversionData: activeDocumentDraft?.conversionData }
          : draftOrPath || activeDocumentDraft;

      if (!draft?.openPath && !draft?.conversionData && !draft?.docType) return;

      if (draft?.conversionData) {
        persistAgentDocumentDraft(draft.conversionData);
      }

      setActiveDocumentDraft(draft);
      setIsDocumentFormModalOpen(true);
    },
    [activeDocumentDraft],
  );

  const handleDocumentSaved = useCallback(
    (payload) => {
      setIsDocumentFormModalOpen(false);
      setActiveDocumentDraft(null);
      appendEntry({
        kind: 'result',
        text: payload?.documentNumber
          ? cp('document_saved_number', { number: payload.documentNumber })
          : cp('document_saved'),
      });
    },
    [appendEntry],
  );

  const handleCloseDocumentFormModal = useCallback(() => {
    setIsDocumentFormModalOpen(false);
  }, []);

  const handleConfirm = async () => {
    if (!pendingConfirmation) return;
    setIsProcessing(true);
    const result = await aiAgentService.confirmPendingAction();
    setIsProcessing(false);
    setPendingConfirmation(null);

    if (result.success) {
      if (result.handoff && result.conversionData) {
        const draftView = await buildAgentDocumentDraftView({
          docType: result.docType,
          conversionData: result.conversionData,
          openPath: result.openPath,
        });
        appendEntry({
          kind: 'document-draft',
          text: result.message || cp('draft_prepared'),
          documentDraft: draftView,
          executionResult: result,
        });
        setActiveDocumentDraft(draftView);
      } else {
        appendEntry({
          kind: 'result',
          text: result.message || cp('action_executed'),
          executionResult: result,
        });

        if (result.openPath) {
          handleOpenDocumentEditor({
            openPath: result.openPath,
            conversionData: result.conversionData,
            docType: result.docType,
          });
        }
      }
    } else {
      appendEntry({ kind: 'error', text: result.error || cp('failure') });
    }
  };

  const handleOpenDocumentHandoff = handleOpenDocumentEditor;

  const handleCloseDocumentDraft = handleCloseDocumentFormModal;

  const handleDraftQuickAction = useCallback(
    (_actionId, draft) => {
      handleOpenDocumentEditor(draft);
    },
    [handleOpenDocumentEditor],
  );

  const handleCancel = async () => {
    await aiAgentService.rejectPendingAction();
    setPendingConfirmation(null);
    appendEntry({ kind: 'result', text: cp('action_cancelled') });
  };

  const handleCancelPlan = () => {
    setPendingPlan(null);
    appendEntry({ kind: 'result', text: cp('plan_cancelled') });
  };

  const handleBuildPlan = async () => {
    if (!pendingPlan || isBuildingPlan || isProcessing) return;

    setIsBuildingPlan(true);
    setAgentMode('agent');
    appendEntry({
      kind: 'result',
      text: cp('plan_approved'),
    });

    const savedPlan = pendingPlan;
    setPendingPlan(null);

    const result = await aiAgentService.executeApprovedPlan(savedPlan, { moduleId });

    setIsBuildingPlan(false);

    if (result.success) {
      appendEntry({
        kind: 'result',
        text: result.response,
        executionResult: result.executionResult,
      });

      if (result.needsConfirmation && result.actionDetected) {
        setPendingConfirmation({
          action: result.actionDetected,
          message:
            result.confirmationMessage || cp('confirm_action'),
        });
      }
    } else {
      appendEntry({
        kind: 'error',
        text: result.error || cp('plan_execution_error'),
      });
    }
  };

  const handleClearHistory = () => {
    entries.forEach((entry) => {
      if (entry.imagePreviewUrl) {
        URL.revokeObjectURL(entry.imagePreviewUrl);
      }
    });
    revokeAttachmentPreview(pendingAttachment);
    setPendingAttachment(null);
    setAttachmentError('');
    aiAgentService.clearHistory();
    setEntries([]);
    setPendingConfirmation(null);
    setPendingPlan(null);
    setActiveDocumentDraft(null);
    setIsDocumentFormModalOpen(false);
    setActiveSessionId(null);
    refreshRecentSessions();
  };

  const handleOpenSession = useCallback(
    (sessionId) => {
      const session = loadAgentSessions(moduleId).find((item) => item.id === sessionId);
      if (!session) return;

      entries.forEach((entry) => {
        if (entry.imagePreviewUrl) {
          URL.revokeObjectURL(entry.imagePreviewUrl);
        }
      });

      aiAgentService.clearHistory();
      setPendingConfirmation(null);
      setPendingPlan(null);
      setActiveDocumentDraft(null);
      setIsDocumentFormModalOpen(false);
      setPendingAttachment(null);
      setAttachmentError('');

      const restoredEntries = (session.entries || []).map((entry, index) => ({
        ...entry,
        id: `${session.id}-${index}`,
      }));

      setEntries(restoredEntries);
      setActiveSessionId(session.id);
      upsertAgentSession(moduleId, session.id, {
        updatedAt: new Date().toISOString(),
      });
      refreshRecentSessions();
      setTimeout(() => inputRef.current?.focus(), 80);
    },
    [entries, moduleId, refreshRecentSessions],
  );

  const handleToggleSessionShare = useCallback(
    (sessionId) => {
      const session = loadAgentSessions(moduleId).find((item) => item.id === sessionId);
      if (!session) return;

      upsertAgentSession(moduleId, sessionId, {
        shared: !session.shared,
      });
      refreshRecentSessions();
    },
    [moduleId, refreshRecentSessions],
  );

  const handleDeleteSession = useCallback(
    (sessionId) => {
      deleteAgentSession(moduleId, sessionId);

      if (activeSessionIdRef.current === sessionId) {
        entries.forEach((entry) => {
          if (entry.imagePreviewUrl) {
            URL.revokeObjectURL(entry.imagePreviewUrl);
          }
        });
        aiAgentService.clearHistory();
        setEntries([]);
        setPendingConfirmation(null);
        setPendingPlan(null);
        setActiveSessionId(null);
      }

      refreshRecentSessions();
    },
    [entries, moduleId, refreshRecentSessions],
  );

  const handleAttachmentSelect = useCallback((file) => {
    const validationError = validateDocumentAttachmentFile(file);
    if (validationError) {
      setAttachmentError(validationError);
      return;
    }

    setPendingAttachment((prev) => {
      revokeAttachmentPreview(prev);
      return createAttachmentPreview(file);
    });
    setAttachmentError('');
  }, []);

  const handleAttachmentClear = useCallback(() => {
    setPendingAttachment((prev) => {
      revokeAttachmentPreview(prev);
      return null;
    });
    setAttachmentError('');
  }, []);

  const handleAttachmentDrop = useCallback(
    (files) => {
      const file = files?.[0];
      if (!file) return;
      handleAttachmentSelect(file);
    },
    [handleAttachmentSelect],
  );

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceStart = () => {
    if (!isVoiceSupported || !isReady || isProcessing || isVoiceSession) return;
    startListening(inputMessage);
  };

  const handleVoiceStop = () => {
    if (!isListening) return;
    stopListening();
  };

  const handleVoiceCancel = () => {
    if (!isListening) return;
    cancelListening();
  };

  return {
    contextLabel,
    activeModuleId: moduleId,
    activeModule,
    setActiveModuleId,
    isReady,
    licenseBlocked,
    entries,
    inputMessage,
    setInputMessage,
    isProcessing,
    pendingConfirmation,
    pendingPlan,
    isBuildingPlan,
    inputRef,
    isVoiceSupported,
    isListening,
    isTranscribing,
    recordingMs,
    audioLevels,
    voiceError,
    isVoiceSession,
    appendEntry,
    handleSend,
    handleConfirm,
    handleCancel,
    handleBuildPlan,
    handleCancelPlan,
    handleClearHistory,
    handleOpenSession,
    handleToggleSessionShare,
    handleDeleteSession,
    recentSessions,
    activeSessionId,
    handleOpenDocumentHandoff,
    activeDocumentDraft,
    isDocumentFormModalOpen,
    handleCloseDocumentDraft,
    handleCloseDocumentFormModal,
    handleOpenDocumentEditor,
    handleDraftQuickAction,
    handleDocumentSaved,
    onInputKeyDown,
    handleVoiceStart,
    handleVoiceStop,
    handleVoiceCancel,
    stopListening,
    cancelListening,
    agentMode,
    setAgentMode,
    pendingAttachment,
    attachmentError,
    handleAttachmentSelect,
    handleAttachmentClear,
    handleAttachmentDrop,
  };
}
